import type { Product, Variant } from '@nucleus-commerce/api-client/admin';

type Option = Product['options'][number];

export interface VariantGroup {
  /** The option value code; empty when the rows are not grouped. */
  key: string;
  label: string;
  variants: Variant[];
}

/**
 * A value no longer on the option has only its code. The variant's title still spells it as it
 * was labelled; otherwise the code's words read better than the code.
 */
function readableCode(code: string, title: string): string {
  const words = code.split(/[_-]+/).filter(Boolean);
  const pattern = words.map((word) => word.replace(/[^a-z0-9]/gi, '\\$&')).join('[^a-z0-9]+');
  const inTitle = pattern
    ? new RegExp(`(?<![a-z0-9])${pattern}(?![a-z0-9])`, 'i').exec(title)
    : null;
  if (inTitle && inTitle[0] !== code) return inTitle[0];
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

/** What a variant holds for an option, as staff read it. */
export function optionValueLabel(option: Option, variant: Variant): string {
  const code = variant.optionValues[option.key] ?? '';
  return (
    option.values.find((value) => value.value === code)?.label ?? readableCode(code, variant.title)
  );
}

/**
 * `null` is the person's "No grouping", `undefined` no choice yet. One option makes no groups: a
 * group per variant says nothing. A choice whose option has gone falls back to the first option.
 */
export function resolveGroupKey(
  options: readonly Option[],
  choice: string | null | undefined,
): string | null {
  if (options.length < 2 || choice === null) return null;
  return options.find((option) => option.key === choice)?.key ?? options[0]?.key ?? null;
}

/** Keeps the rows' order inside a group; groups follow the option's own value order. */
export function groupVariants(
  variants: readonly Variant[],
  options: readonly Option[],
  groupKey: string | null,
): VariantGroup[] {
  const option = options.find((candidate) => candidate.key === groupKey);
  if (!option) return variants.length > 0 ? [{ key: '', label: '', variants: [...variants] }] : [];
  const groups = new Map<string, VariantGroup>();
  for (const variant of variants) {
    const key = variant.optionValues[option.key] ?? '';
    const group = groups.get(key);
    if (group) group.variants.push(variant);
    else groups.set(key, { key, label: optionValueLabel(option, variant), variants: [variant] });
  }
  const rank = (key: string) => {
    const at = option.values.findIndex((value) => value.value === key);
    // A value no longer on the option (an archived variant's) goes last.
    return at < 0 ? option.values.length : at;
  };
  return [...groups.values()].sort((a, b) => rank(a.key) - rank(b.key));
}

/** A row under a group names only what its group does not. */
export function variantRowLabel(
  variant: Variant,
  options: readonly Option[],
  groupKey: string | null,
): string {
  if (groupKey === null) return variant.title;
  const rest = options
    .filter((option) => option.key !== groupKey)
    .map((option) => optionValueLabel(option, variant))
    .filter((label) => label !== '');
  return rest.length > 0 ? rest.join(' / ') : variant.title;
}

export function matchesVariantSearch(variant: Variant, sku: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return needle === '' || `${variant.title} ${sku}`.toLowerCase().includes(needle);
}

const NEW_FOR_MS = 15 * 60_000;
const SAME_SAVE_MS = 60_000;

/**
 * Created in the last minutes, after the product itself: what saving options has just added at
 * the base price and someone may want to reprice. A new product's own variants are not news.
 */
export function isNewVariant(
  variant: Pick<Variant, 'createdAt'>,
  product: Pick<Product, 'createdAt'>,
  now: number,
): boolean {
  const created = Date.parse(variant.createdAt);
  return now - created < NEW_FOR_MS && created - Date.parse(product.createdAt) > SAME_SAVE_MS;
}

/** The fields a variant row shows without View more. */
export const MASTER_ROW_FIELDS: ReadonlySet<string> = new Set(['price', 'sku']);

export interface VariantReveal {
  /** The first row holding an error, to bring into view. */
  first: string;
  /** The first row whose error is under View more, to open. */
  expand: string | null;
}

/** Where a failed save sends the eye; `null` when no row holds an error. */
export function revealFor(
  order: readonly string[],
  errors: ReadonlyMap<string, readonly string[]>,
  rowFields: ReadonlySet<string> = MASTER_ROW_FIELDS,
): VariantReveal | null {
  const failed = order.filter((id) => (errors.get(id)?.length ?? 0) > 0);
  const first = failed[0];
  if (first === undefined) return null;
  const expand = failed.find((id) => errors.get(id)?.some((field) => !rowFields.has(field)));
  return { first, expand: expand ?? null };
}
